import React from 'react';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';
import { Star } from '@mui/icons-material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Link } from 'react-router-dom';
import CalculateCharges from '../../../../Bookings/Fees/CalculateCharges';
import { ExperienceDetails } from '../../Shared/types';
import { iconForType } from '../../Shared/iconForType';

type Props = {
    experienceDetails: ExperienceDetails;
    fullAddress: string;
    label: string;
    value: string;
    fees: any;
};

export default function RightSummary({
    experienceDetails,
    fullAddress,
    label,
    value,
    fees,
}: Props) {
    const Icon = iconForType(experienceDetails?.type);
    const theme = useTheme();

    return (
        <Stack gap={{ xs: 1.5, sm: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: { xs: 1.5, sm: 2 },
                }}
            >
                <Box
                    component='img'
                    src={experienceDetails.photos?.[0]?.src}
                    alt='experiencePhoto'
                    sx={{
                        width: { xs: 56, sm: 72, md: 84 },
                        height: { xs: 56, sm: 72, md: 84 },
                        borderRadius: 1.5,
                        objectFit: 'cover',
                        display: 'block',
                        flex: '0 0 auto',
                    }}
                />
                <Stack gap={{ xs: 0.75, sm: 1 }} sx={{ width: '100%' }}>
                    <Typography
                        variant='subHeading'
                        sx={{
                            fontWeight: 'bold',
                            fontSize: {
                                xs: '1rem',
                                sm: '1.125rem',
                                md: '1.25rem',
                            },
                            lineHeight: 1.2,
                        }}
                    >
                        {experienceDetails?.name}
                    </Typography>

                    <Stack
                        direction='row'
                        alignItems='center'
                        flexWrap='wrap'
                        gap={{ xs: 1, sm: 2 }}
                        sx={{ rowGap: { xs: 0.5, sm: 1 } }}
                    >
                        {typeof experienceDetails?.rating === 'number' && (
                            <Typography
                                variant='body1'
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                }}
                            >
                                <Star
                                    sx={{
                                        fontSize: 16,
                                        color: 'text.primary',
                                    }}
                                />
                                {Number(experienceDetails?.rating || 0).toFixed(
                                    1,
                                )}
                            </Typography>
                        )}

                        <Typography
                            variant='body1'
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                            }}
                        >
                            <Icon
                                sx={{ fontSize: 16, color: 'text.primary' }}
                            />
                            {(experienceDetails?.type ?? '').replace(
                                /^./,
                                (c) => c.toUpperCase(),
                            )}
                        </Typography>

                        <Typography
                            variant='body1'
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                maxWidth: { xs: '100%', sm: '60%' },
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            }}
                            title={`${experienceDetails?.suburb} ${experienceDetails?.state}, ${experienceDetails?.countryCode}`}
                        >
                            <LocationOnIcon
                                sx={{ fontSize: 16, color: 'text.primary' }}
                            />
                            <Link
                                style={{ color: 'text.primary' }}
                                target='_blank'
                                to={`https://www.google.com/maps/place/${fullAddress}`}
                            >
                                {experienceDetails?.suburb}{' '}
                                {experienceDetails?.state},{' '}
                                {experienceDetails?.countryCode}
                            </Link>
                        </Typography>
                    </Stack>
                </Stack>
            </Box>

            <Box>
                <Stack gap={0.5}>
                    <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                        {label}
                    </Typography>
                    <Typography variant='body1'>{value}</Typography>
                </Stack>

                <Divider sx={{ mt: 1, color: theme.palette.faded.main }} />
            </Box>

            <Box>
                {experienceDetails?.charges.length !== 0 &&
                    !experienceDetails?.billingExcluded && (
                        <Box sx={{ pt: 2 }}>
                            <Stack gap={1}>
                                <Typography
                                    variant='body1'
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    Estimated Pricing
                                </Typography>
                                <CalculateCharges
                                    fees={fees}
                                    moduleId={experienceDetails?.id}
                                    moduleType={experienceDetails?.type}
                                    isWaitingList
                                    spaces={[]}
                                />
                            </Stack>

                            <Divider
                                sx={{
                                    mt: 1,
                                    color: theme.palette.faded.main,
                                    marginTop: '20px',
                                    mb: '20px',
                                }}
                            />
                        </Box>
                    )}
            </Box>
        </Stack>
    );
}
